import { D_REF_MIDI } from './keyboard-layouts';

function oklchToRgb(L: number, C: number, H: number): [number, number, number] {
  const hRad = H * Math.PI / 180;
  const a = C * Math.cos(hRad);
  const b = C * Math.sin(hRad);


  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b;


  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;


  const lr = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const lg = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const lb = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s;


  const gamma = (x: number): number =>
    x >= 0.0031308 ? 1.055 * Math.pow(x, 1 / 2.4) - 0.055 : 12.92 * x;

  return [
    Math.round(Math.max(0, Math.min(1, gamma(lr))) * 255),
    Math.round(Math.max(0, Math.min(1, gamma(lg))) * 255),
    Math.round(Math.max(0, Math.min(1, gamma(lb))) * 255),
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

function oklch(L: number, C: number, H: number): string {
  return rgbToHex(...oklchToRgb(L, C, H));
}


function pcHue(pc: number): number {
  return (pc * 30 + 329) % 360;
}

function chromaticHue(coordX: number): number {
  const pc = ((2 + coordX * 7) % 12 + 12) % 12;
  return pcHue(pc);
}

export function pitchCentsToHue(pitchCents: number): number {
  const centsInOctave = ((pitchCents % 1200) + 1200) % 1200;
  return (centsInOctave / 1200 * 360 + 29) % 360;
}


const COF_FROM_PC: readonly number[] = [
  -2,  // C
   5,  // C#
   0,  // D
  -5,  // D#/Eb
   2,  // E
  -3,  // F
   4,  // F#
  -1,  // G
   6,  // G#/Ab
   1,  // A
  -4,  // A#/Bb
   3,  // B
];


export const NOTE_COLORS: readonly string[] = Array.from({ length: 12 }, (_, pc) =>
  oklch(0.72, 0.19, pcHue(pc)));

export const NOTE_COLORS_DIM: readonly string[] = Array.from({ length: 12 }, (_, pc) =>
  oklch(0.32, 0.07, pcHue(pc)));


export function noteColor(midiNote: number, alpha = 1): string {
  const pc = ((midiNote % 12) + 12) % 12;
  if (alpha === 1) return NOTE_COLORS[pc];
  const [r, g, b] = oklchToRgb(0.72, 0.19, pcHue(pc));
  return `rgba(${r},${g},${b},${alpha})`;
}

export function colorFromCoordX(coordX: number): string {
  return oklch(0.72, 0.19, chromaticHue(coordX));
}

export function cellColors(
  coordX: number,
  state: 'active' | 'target' | 'target-pressed' | 'sustained' | 'uncalibrated-white' | 'uncalibrated-black' | 'white' | 'black',
  pitchCents = 0,
): { fill: string; text: string } {
  const h = pitchCents !== 0 ? pitchCentsToHue(pitchCents) : chromaticHue(coordX);
  switch (state) {
    case 'active':
      return { fill: oklch(0.72, 0.19, h), text: '#ffffff' };
    case 'target':
      return { fill: oklch(0.96, 0.03, h), text: '#000000' };
    case 'target-pressed':
      return { fill: oklch(0.55, 0.01, h), text: oklch(0.80, 0.01, h) };
    case 'sustained':
      return { fill: oklch(0.38, 0.11, h), text: oklch(0.82, 0.16, h) };
    case 'uncalibrated-white':
      return { fill: oklch(0.24, 0, h), text: oklch(0.50, 0, h) };
    case 'uncalibrated-black':
      return { fill: oklch(0.24, 0, h), text: oklch(0.50, 0, h) };
    case 'white':
      return { fill: oklch(0.24, 0.055, h), text: oklch(0.75, 0.14, h) };
    case 'black':
      return { fill: oklch(0.24, 0.055, h), text: oklch(0.75, 0.14, h) };
  }
}

export function lerpHue(h0: number, h1: number, t: number): number {
  const ct = Math.max(0, Math.min(1, t));
  let diff = h1 - h0;
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;
  return ((h0 + diff * ct) % 360 + 360) % 360;
}

export function cellColorsFromHue(
  hue: number,
  state: 'active' | 'target' | 'target-pressed' | 'sustained' | 'uncalibrated-white' | 'uncalibrated-black' | 'white' | 'black',
): { fill: string; text: string } {
  const h = hue;
  switch (state) {
    case 'active':
      return { fill: oklch(0.72, 0.19, h), text: '#ffffff' };
    case 'target':
      return { fill: oklch(0.96, 0.03, h), text: '#000000' };
    case 'target-pressed':
      return { fill: oklch(0.55, 0.01, h), text: oklch(0.80, 0.01, h) };
    case 'sustained':
      return { fill: oklch(0.38, 0.11, h), text: oklch(0.82, 0.16, h) };
    case 'uncalibrated-white':
      return { fill: oklch(0.24, 0, h), text: oklch(0.50, 0, h) };
    case 'uncalibrated-black':
      return { fill: oklch(0.24, 0, h), text: oklch(0.50, 0, h) };
    case 'white':
      return { fill: oklch(0.24, 0.055, h), text: oklch(0.75, 0.14, h) };
    case 'black':
      return { fill: oklch(0.24, 0.055, h), text: oklch(0.75, 0.14, h) };
  }
}

export function pitchClassFromCoordX(coordX: number): number {
  return ((2 + coordX * 7) % 12 + 12) % 12;
}

export function coordToMidiNote(coordX: number, coordY: number): number {

  return D_REF_MIDI + coordX * 7 + coordY * 12;
}

export function midiToCoord(midi: number): [number, number] {
  const pitchClass = ((midi % 12) + 12) % 12;
  const x = COF_FROM_PC[pitchClass];
  const pitchCents = (midi - D_REF_MIDI) * 100;
  const y = Math.round((pitchCents - x * 700) / 1200);
  return [x, y];
}
